import { useNavigate } from 'react-router-dom'
import { Package } from '@phosphor-icons/react'
import { Badge, DataTable, EmptyState, type Column } from '@/ui'
import { errorMessage } from '../../api/core'
import { stepLabel, unitTypeLabel, useBatches, type BatchRow } from '../../api/process'
import { useCrops } from '../../api/reference'
import { useSites } from '../../api/store'
import { FilterBar, SearchFilter, SelectFilter } from '../../components/FilterBar'
import { ConsolePage } from '../../components/Layout'
import { Code, Kg, RefLink, Status, Stream, When } from '../../components/bits'
import { holdText } from '../../model/process/hold'
import { useUrlState } from '../../lib/useUrlState'

const LIMIT = 25

/** Batches: everything a machine run made (batches, by-products, rejects, pack units). */
export function BatchesPage() {
  const navigate = useNavigate()
  const [f, setF, { reset, active }] = useUrlState({
    search: '',
    unitType: '',
    stream: '',
    hold: '',
    status: '',
    cropId: '',
    locationId: '',
    runId: '',
    page: 1,
  })
  const crops = useCrops()
  const sites = useSites()
  const cropName = new Map((crops.data ?? []).map((c) => [c.id, c.name]))
  const q = useBatches({
    page: f.page,
    limit: LIMIT,
    search: f.search || undefined,
    unitType: f.unitType || undefined,
    stream: f.stream || undefined,
    hold: f.hold || undefined,
    status: f.status || undefined,
    cropId: f.cropId || undefined,
    locationId: f.locationId || undefined,
    runId: f.runId || undefined,
  })

  const columns: Column<BatchRow>[] = [
    {
      id: 'code',
      header: 'Batch',
      cell: (r) => (
        <span className="flex flex-wrap items-center gap-1.5">
          <Code>{r.code}</Code>
          {r.isFlush && <Badge>Flush</Badge>}
          {r.grade && <Badge tone="accent">Grade {r.grade}</Badge>}
        </span>
      ),
    },
    { id: 'type', header: 'Kind', cell: (r) => unitTypeLabel(r.unitType), hideBelow: 'sm' },
    {
      id: 'run',
      header: 'Run',
      cell: (r) =>
        r.runId ? (
          <span className="flex flex-col leading-tight">
            <RefLink to={`/c/runs/${r.runId}`}>{r.runCode ?? 'run'}</RefLink>
            <span className="text-[12px] text-muted">{stepLabel(r.stepCode)}</span>
          </span>
        ) : (
          '—'
        ),
      hideBelow: 'md',
    },
    { id: 'crop', header: 'Crop', cell: (r) => (r.cropId ? (cropName.get(r.cropId) ?? '—') : '—'), hideBelow: 'lg' },
    {
      id: 'origin',
      header: 'Origin',
      cell: (r) =>
        r.stream === 'CONV' ? (
          <span className="text-muted">Ordinary</span>
        ) : (
          <span className="flex flex-col leading-tight">
            <span className="font-mono text-[13px]">{r.farmerCode ?? (r.farmerId ? 'One farmer' : '—')}</span>
            {r.seasonCode && <span className="font-mono text-[11.5px] text-muted">{r.seasonCode}</span>}
          </span>
        ),
      hideBelow: 'lg',
    },
    { id: 'stream', header: 'Stream', cell: (r) => <Stream stream={r.stream} />, hideBelow: 'md' },
    { id: 'kg', header: 'Kg', align: 'right', cell: (r) => <Kg grams={r.netG} /> },
    { id: 'sacks', header: 'Sacks', align: 'right', mono: true, cell: (r) => r.bagCount, hideBelow: 'sm' },
    {
      id: 'status',
      header: 'Status',
      cell: (r) => (
        <span className="flex flex-wrap items-center gap-1">
          <Status status={r.status} />
          {r.hold !== 'NONE' && <Status status={r.hold} label={holdText(r.hold, r.holdReasons)} />}
        </span>
      ),
    },
    { id: 'made', header: 'Made', cell: (r) => <When at={r.committedAt} />, hideBelow: 'lg' },
  ]

  return (
    <ConsolePage
      eyebrow="Process"
      title="Batches"
      description="What each machine run made: batches, by-products, rejects and pack units, with their hold, origin and sacks. Open one to trace it back to the farmers' lots or forward to where it went."
    >
      <DataTable<BatchRow>
        caption="Batches"
        columns={columns}
        rows={q.data?.rows ?? []}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        onRowClick={(r) => navigate(`/c/batches/${r.id}`)}
        pageSize={LIMIT}
        server={{ page: f.page, total: q.data?.total ?? 0, onPageChange: (page) => setF({ page }) }}
        filters={
          <FilterBar onClear={reset} active={active}>
            <SearchFilter label="Code" value={f.search} onChange={(search) => setF({ search })} placeholder="e.g. B-261104" />
            <SelectFilter
              label="Kind"
              value={f.unitType}
              onChange={(unitType) => setF({ unitType })}
              options={['BATCH', 'BYPRODUCT', 'REJECT', 'PACK'].map((t) => ({ value: t, label: unitTypeLabel(t) }))}
            />
            <SelectFilter
              label="Stream"
              value={f.stream}
              onChange={(stream) => setF({ stream })}
              options={[
                { value: 'MAIN', label: 'Good' },
                { value: 'CONV', label: 'Ordinary' },
              ]}
            />
            <SelectFilter
              label="Hold"
              value={f.hold}
              onChange={(hold) => setF({ hold })}
              options={[
                { value: 'NONE', label: 'Not on hold' },
                { value: 'QUARANTINE', label: 'On hold' },
                { value: 'HELD', label: 'Held (release)' },
                { value: 'RECALLED', label: 'Recalled' },
              ]}
            />
            <SelectFilter
              label="Status"
              value={f.status}
              onChange={(status) => setF({ status })}
              options={['DRAFT', 'COMMITTED', 'STORED', 'CONSUMED', 'VOID'].map((s) => ({ value: s, label: s === 'CONSUMED' ? 'Used up' : s.charAt(0) + s.slice(1).toLowerCase() }))}
            />
            <SelectFilter label="Crop" value={f.cropId} onChange={(cropId) => setF({ cropId })} options={(crops.data ?? []).map((c) => ({ value: c.id, label: c.name }))} />
            <SelectFilter
              label="Site"
              value={f.locationId}
              onChange={(locationId) => setF({ locationId })}
              options={(sites.data ?? []).map((s) => ({ value: s.id, label: s.name || s.code }))}
            />
            {f.runId && (
              <button type="button" className="h-9 rounded-chip border border-rule px-2 font-mono text-[11px]" onClick={() => setF({ runId: '' })}>
                One run only ✕
              </button>
            )}
          </FilterBar>
        }
        empty={<EmptyState icon={<Package />} title={active ? 'No batches match' : 'No batches yet'} description="Batches are made when a machine run closes." />}
      />
    </ConsolePage>
  )
}
